/* eslint-disable */
import { useState, useEffect } from "react";
import {
  Row,
  Col,
  Container,
  Card,
  Badge,
  Spinner,
  Button,
  Alert,
} from "react-bootstrap";
import {
  FaLock,
  FaCircleExclamation,
  FaEye,
  FaUser,
  FaCalendarDay,
  FaEnvelope,
  FaGears,
  FaTriangleExclamation,
} from "react-icons/fa6";
import { FaHistory } from "react-icons/fa";
import dayjs from "dayjs";
import ViewerItem from "./ViewerItem";
import InterestedPartiesViewer from "./InterestedPartiesViewer";
import PagerDutyActivity from "../Modern/IncidentDetails/PagerDutyActivity";
import { hasEditorAccess } from "../../utils/privilegeHelpers";
import {
  hasIncidentLinkageContext,
  resolveIncidentDisplayWithPlaceholder,
} from "../../utils/incidentLinkage";
import { TimelineAPI } from "../../connections/Timeline";

// Dayjs plugins
const relativeTime = require("dayjs/plugin/relativeTime");
const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");
const advancedFormat = require("dayjs/plugin/advancedFormat");

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);

const dateFormat = "MM/DD/YYYY HH:mm z";
const tz = "America/New_York";

/**
 * ChronoViewer Component
 * Displays incident information with a unified chronological timeline view
 * combining notes, communications, and workflows in reverse chronological order.
 *
 * This component is shown when the "chronological" feature toggle is enabled.
 */
function ChronoViewer(props) {
  const outage = props.outage;
  const slackURL = props.slackURL;
  const problem = props.problem;
  const user = props.user;
  const impactRefreshTrigger = props.impactRefreshTrigger;

  const awsCase = outage && outage.awscase ? outage.awscase : "";

  // Timeline state
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [totalEvents, setTotalEvents] = useState(0);
  const limit = 50;

  // Use full width for single-column layout
  const width = 12;

  // Fetch timeline data on mount
  useEffect(() => {
    const fetchTimeline = async () => {
      // Debug logging
      console.log("ChronoViewer: Checking props for timeline fetch", {
        problem: problem,
        problemNumber: problem?.number,
        outage: outage,
        outageId: outage?.id,
      });

      if (!problem || !problem.number || !outage || !outage.number) {
        console.warn(
          "ChronoViewer: Missing required props for timeline fetch",
          {
            hasProblem: !!problem,
            hasProblemNumber: !!problem?.number,
            hasOutage: !!outage,
            hasOutageId: !!outage?.id,
          },
        );
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        console.log("ChronoViewer: Fetching timeline", {
          problemNumber: problem.number,
          outageId: outage.number,
          limit,
        });

        const response = await TimelineAPI.getConsolidatedTimeline(
          problem.number,
          outage.number,
          limit,
          0,
        );

        console.log("ChronoViewer: Timeline response received", {
          eventsCount: response.events?.length || 0,
          total: response.total,
        });

        setTimelineEvents(response.events || []);
        setTotalEvents(response.total || 0);
        setOffset(0);
        setHasMore(
          (response.events || []).length >= limit && response.total > limit,
        );
      } catch (err) {
        console.error("ChronoViewer: Error fetching timeline:", err);
        setError(err.message || "Failed to load timeline");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTimeline();
  }, [problem, outage, limit]);

  /**
   * Retry fetching timeline after an error
   */
  const retryFetch = () => {
    setError(null);
    setIsLoading(true);

    const fetchTimeline = async () => {
      if (!problem || !problem.number || !outage || !outage.number) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await TimelineAPI.getConsolidatedTimeline(
          problem.number,
          outage.number,
          limit,
          0,
        );

        setTimelineEvents(response.events || []);
        setTotalEvents(response.total || 0);
        setOffset(0);
        setHasMore(
          (response.events || []).length >= limit && response.total > limit,
        );
        setError(null);
      } catch (err) {
        console.error("Error fetching timeline:", err);
        setError(err.message || "Failed to load timeline");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTimeline();
  };

  /**
   * Load more timeline events (pagination)
   */
  const loadMoreEvents = async () => {
    if (
      !problem ||
      !problem.number ||
      !outage ||
      !outage.number ||
      isLoadingMore ||
      !hasMore
    ) {
      return;
    }

    try {
      setIsLoadingMore(true);

      const newOffset = offset + limit;
      const response = await TimelineAPI.getConsolidatedTimeline(
        problem.number,
        outage.number,
        limit,
        newOffset,
      );

      // Append new events to existing timeline
      setTimelineEvents((prev) => [...prev, ...(response.events || [])]);
      setOffset(newOffset);
      setTotalEvents(response.total || 0);

      // Check if there are more events to load
      setHasMore(
        (response.events || []).length >= limit &&
          newOffset + limit < response.total,
      );
    } catch (err) {
      console.error("Error loading more timeline events:", err);
      // Don't set main error state, just log for debugging
      // User can retry by clicking Load More again
    } finally {
      setIsLoadingMore(false);
    }
  };

  /**
   * Render a timeline event based on its type
   */
  const renderTimelineEvent = (event) => {
    const eventTime = dayjs(event.timestamp).tz(tz);

    switch (event.eventType) {
      case "note":
        return renderNoteEvent(event, eventTime);
      case "communication":
        return renderCommunicationEvent(event, eventTime);
      case "workflow":
        return renderWorkflowEvent(event, eventTime);
      default:
        return null;
    }
  };

  /**
   * Render a note event
   */
  const renderNoteEvent = (event, eventTime) => {
    const note = event.data;

    // Check if this is an impact report note (starts with "Impact report submitted:")
    const isImpactNote =
      note.message && note.message.startsWith("Impact report submitted:");

    if (isImpactNote) {
      return renderImpactNoteEvent(event, eventTime, note);
    }

    const multiline = note.message
      ? note.message.split("\n").map((line, index) => (
          <p key={index} className="mb-1">
            {line}
          </p>
        ))
      : "";

    return (
      <Row key={event.id} className="mb-3">
        <Col>
          <Card
            className={`${note.private ? "border-secondary" : ""} ${
              note.important ? "border-warning" : ""
            }`}
          >
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <div className="mb-2">
                    {note.private && (
                      <Badge bg="secondary" className="me-1">
                        <FaLock className="me-1" />
                        Private
                      </Badge>
                    )}
                    {note.important && (
                      <Badge bg="warning" text="dark" className="me-1">
                        <FaCircleExclamation className="me-1" />
                        Important
                      </Badge>
                    )}
                    {note.pir && (
                      <Badge bg="primary" className="me-1">
                        PIR
                      </Badge>
                    )}
                    {!note.private && (
                      <Badge bg="success" className="me-1">
                        <FaEye className="me-1" />
                        Public
                      </Badge>
                    )}
                  </div>

                  <div className="mb-2">{multiline}</div>

                  <div className="text-muted small">
                    <FaUser className="me-1" />
                    <span className="me-3">{event.author.name}</span>
                    <FaCalendarDay className="me-1" />
                    <span>{eventTime.format(dateFormat)}</span>
                    <span className="ms-2">({eventTime.fromNow()})</span>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  /**
   * Render an impact report note event with compact formatting
   */
  const renderImpactNoteEvent = (event, eventTime, note) => {
    // Parse impact note fields
    const lines = note.message.split("\n");
    const fields = {};

    lines.forEach((line) => {
      if (line.includes(":")) {
        const [key, value] = line.split(":", 2);
        fields[key.trim()] = value.trim();
      }
    });

    const brand = fields["Brand"] || "";
    const application = fields["Application"] || "";
    const status = fields["Status"] || "";

    // Debug logging to see what we're parsing
    console.log("Impact note parsed fields:", {
      brand,
      application,
      status,
      allFields: fields,
    });

    // Build the brand/application display line
    let brandAppLine = "";
    if (brand && application) {
      brandAppLine = (
        <>
          <strong>{brand}</strong> - {application}
        </>
      );
    } else if (brand) {
      brandAppLine = <strong>{brand}</strong>;
    } else if (application) {
      brandAppLine = application;
    } else {
      brandAppLine = (
        <em className="text-muted">No brand/application specified</em>
      );
    }

    return (
      <Row key={event.id} className="mb-3">
        <Col>
          <Card className="border-info">
            <Card.Body className="py-2">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <div className="mb-2">
                    <Badge bg="info" className="me-1">
                      Impact Report
                    </Badge>
                    {note.private && (
                      <Badge bg="secondary" className="me-1">
                        <FaLock className="me-1" />
                        Private
                      </Badge>
                    )}
                  </div>

                  <div className="small">
                    <div className="mb-1">{brandAppLine}</div>
                    {status && (
                      <div className="mb-1">
                        <span className="text-muted">Status:</span> {status}
                      </div>
                    )}
                  </div>

                  <div className="text-muted small mt-2">
                    <FaUser className="me-1" />
                    <span className="me-3">{event.author.name}</span>
                    <FaCalendarDay className="me-1" />
                    <span>{eventTime.format(dateFormat)}</span>
                    <span className="ms-2">({eventTime.fromNow()})</span>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  /**
   * Render a communication event
   */
  const renderCommunicationEvent = (event, eventTime) => {
    const comm = event.data;

    return (
      <Row key={event.id} className="mb-3">
        <Col>
          <Card className="border-success">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <div className="mb-2">
                    <Badge bg="success" className="me-1">
                      <FaEnvelope className="me-1" />
                      Communication
                    </Badge>
                  </div>

                  <h6 className="mb-2">{comm.subject}</h6>

                  {comm.message && (
                    <div className="mb-2">
                      <Card.Text className="text-muted small">
                        {comm.message}
                      </Card.Text>
                    </div>
                  )}

                  <div className="text-muted small">
                    <FaUser className="me-1" />
                    <span className="me-3">{event.author.name}</span>
                    <FaCalendarDay className="me-1" />
                    <span>{eventTime.format(dateFormat)}</span>
                    <span className="ms-2">({eventTime.fromNow()})</span>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  /**
   * Render a workflow event
   */
  const renderWorkflowEvent = (event, eventTime) => {
    const workflow = event.data;

    return (
      <Row key={event.id} className="mb-3">
        <Col>
          <Card className="border-info">
            <Card.Body className="py-2">
              <Row className="align-items-center">
                <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                  <div className="d-flex align-items-center">
                    <Badge bg="success" className="me-2">
                      <FaGears className="me-1" />
                      PD
                    </Badge>
                    <strong>
                      {workflow.workflow ? workflow.workflow.label : "Workflow"}
                    </strong>
                  </div>
                </Col>
                <Col xs={12} sm={12} md={5} lg={4} xl={4}>
                  <div className="d-flex align-items-center text-muted small">
                    <FaCalendarDay className="me-1" />
                    <span>{eventTime.format(dateFormat)}</span>
                    <span className="ms-2">({eventTime.fromNow()})</span>
                  </div>
                </Col>
                <Col xs={12} sm={12} md={3} lg={4} xl={4}>
                  <div className="d-flex align-items-center text-muted small">
                    <FaUser className="me-1" />
                    <span>{event.author.name}</span>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  return (
    <Container fluid>
      {/* Incident Header Section */}
      <Row>
        <Col>
          <ViewerItem
            title="Incident Status"
            id="status"
            value={outage && outage.status ? outage.status : ""}
            width={width}
          />
        </Col>
      </Row>

      {hasEditorAccess(user) &&
        problem &&
        (hasIncidentLinkageContext(
          problem.incidentNumber,
          problem.number,
        ) ||
          awsCase) && (
          <Row>
            <Col>
              <ViewerItem
                title="Incident Number"
                id="incidentNumber"
                value={resolveIncidentDisplayWithPlaceholder(
                  problem.incidentNumber,
                  problem.number,
                  !!awsCase,
                )}
                width={width}
              />
            </Col>
          </Row>
        )}

      {hasEditorAccess(user) &&
        problem &&
        awsCase && (
          <Row>
            <Col>
              <ViewerItem
                title="AWS Case #"
                id="awscase"
                value={awsCase}
                width={width}
              />
            </Col>
          </Row>
        )}

      <Row>
        <Col>
          <ViewerItem
            id="nextUpdate"
            title="Next Update"
            value={outage ? outage.nextUpdate : ""}
            width={width}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <ViewerItem
            title="Current Status"
            id="currentStatus"
            value={
              outage && outage.currentStatus
                ? outage.currentStatus
                : "Loading..."
            }
            width={width}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <ViewerItem
            title="Incident Commander"
            id="incidentCommander"
            value={
              outage && outage.incidentCommander ? outage.incidentCommander : ""
            }
            width={width}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <ViewerItem
            title="Incident Manager"
            id="incidentManager"
            value={outage && outage.mim ? outage.mim : ""}
            width={width}
          />
        </Col>
      </Row>

      {/* Timeline Section */}
      <Row className="mt-4">
        <Col>
          <Row>
            <Col className="headings" xs={12}>
              Timeline
            </Col>
          </Row>
          <Row>
            <Col>
              {isLoading && (
                <div className="text-center text-muted p-5">
                  <Spinner animation="border" role="status" className="mb-3">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p>Loading timeline...</p>
                </div>
              )}

              {error && (
                <Alert variant="danger" className="m-3">
                  <Alert.Heading>
                    <FaTriangleExclamation className="me-2" />
                    Failed to Load Timeline
                  </Alert.Heading>
                  <p className="mb-3">{error}</p>
                  <div className="d-flex gap-2">
                    <Button variant="outline-danger" onClick={retryFetch}>
                      Retry
                    </Button>
                  </div>
                </Alert>
              )}

              {!isLoading && !error && timelineEvents.length === 0 && (
                <div className="text-center text-muted p-5">
                  <FaHistory size={48} className="mb-3" />
                  <p>No timeline events yet</p>
                </div>
              )}

              {!isLoading && !error && timelineEvents.length > 0 && (
                <Container>
                  <Row>
                    <Col>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="text-muted">
                          Showing {timelineEvents.length} of {totalEvents} event
                          {totalEvents !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </Col>
                  </Row>

                  {timelineEvents.map((event) => renderTimelineEvent(event))}

                  {/* Load More Button for Pagination */}
                  {hasMore && (
                    <Row className="mt-3">
                      <Col className="text-center">
                        <Button
                          variant="outline-primary"
                          onClick={loadMoreEvents}
                          disabled={isLoadingMore}
                        >
                          {isLoadingMore ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                              />
                              Loading...
                            </>
                          ) : (
                            "Load More Events"
                          )}
                        </Button>
                      </Col>
                    </Row>
                  )}
                </Container>
              )}
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Interested Parties Section */}
      {outage &&
        outage.interestedParties &&
        outage.interestedParties.length > 0 && (
          <Row className="mt-4">
            <Col>
              <Row>
                <Col className="headings" xs={12}>
                  Interested Parties
                </Col>
              </Row>
              <Row>
                <Col>
                  <InterestedPartiesViewer
                    interestedParties={outage.interestedParties}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      {/* PagerDuty Activity (US1979253) — read-only, collapsed panel at the bottom */}
      {problem && problem.number && outage && outage.number && (
        <Row className="mt-4">
          <Col>
            <PagerDutyActivity
              problemID={problem.number}
              outageID={outage.number}
              isActive={
                !(
                  outage &&
                  outage.status &&
                  String(outage.status).toLowerCase() === "resolve"
                )
              }
            />
          </Col>
        </Row>
      )}
    </Container>
  );
}

export default ChronoViewer;
